/**
 * How a fixed total was arrived at — the quote fold's own arithmetic.
 *
 * `client/projects.html` §1 and `client/requests.html` §2 draw the same build-up for
 * the same work order: the rate-card lines, the tiered subtotal, the retainer
 * discount, a currency conversion where there is one, the rounding, and the total the
 * client approved. Both pages read it from `GET /api/clients/me/{projects,requests}/:ref`
 * — `lines` and the `project` entry of `pricing` — and session 19 shipped it written
 * out twice, ninety lines each, differing only in two labels and where the total goes.
 *
 * **The rows are the shared thing, not the table.** The projects page appends its scope
 * changes underneath them, so it owns its `<Table>` and takes `QuoteBuildUpRows`; a
 * decision card has nothing else in its fold and takes `QuoteBuildUpTable`. Neither
 * writes a `Tr variant="sub"` of its own again.
 *
 * Every row is a stored field. The mockup's single `−$530.00` discount line is **not**
 * reproduced: `retainer_discount` is −$526.00 and `rounding` is −$4.00, because
 * `pricing.project()` discounts the tiered amount and rounds afterwards, and folding
 * the two together would leave a column that no longer adds up — which is the one thing
 * an arithmetic build-up exists to do.
 *
 * ## `statesTier`, and why the default is off
 *
 * The subtotal row read "Subtotal · market tier 1, ×1.0". The market tier is an input
 * to a price and not a fact a client is shown — `InvoiceDetailsPanel.tsx` §"The market
 * tier is not one of them" is the whole of that decision — so on a client page the row
 * is now "Subtotal" and the amount under it is the one it always was.
 *
 * This component lives under `components/client/` and **the studio also draws it**:
 * `components/studio/Breakdown.tsx` is `QuoteBuildUpTable` for `kind: 'project'`, and a
 * staff reader pricing a quote is exactly who needs the tier stated. So the tier is a
 * prop rather than a deletion, and it is **off by default**: a new client-side caller
 * that forgets it renders the safe thing, and the one caller that wants it says so.
 */

import { isZero, type ComputedPrice, type Money, type WorkOrderLineView } from '@fareast/shared'

import { ScrollX } from '../portal/Layout.js'
import { TBody, THead, Table, Td, Th, Tr } from '../portal/Table.js'
import { Num } from '../portal/Text.js'
import { useFormat } from '../../format/useFormat.js'

/**
 * The `project` entry of a `ComputedPrice`, which is the shape both detail routes
 * answer with. Named here rather than imported from a page, so neither page owns it.
 */
export type ProjectBreakdownView = NonNullable<ComputedPrice['project']>

export type QuoteBuildUpProps = {
  readonly lines: readonly WorkOrderLineView[]
  readonly breakdown: ProjectBreakdownView | null
  /** What the last row says. The two pages word it as their own mockup does. */
  readonly totalLabel: string
  /** What the last row shows. Usually `breakdown.total`; a card states its own. */
  readonly total: Money
  /** What the discount row says — "Signed with a retainer", "Retainer client". */
  readonly discountLabel: string
  /** Render the total at figure size. The projects fold does; a decision card does not. */
  readonly emphasiseTotal?: boolean
  /**
   * Name the market tier on the subtotal row. **Staff surfaces only** — see above.
   *
   * Off by default, so a client page gets the client's answer without asking for it.
   */
  readonly statesTier?: boolean
}

/**
 * The rows, for a page that owns the table around them.
 *
 * Answers `null` when there is nothing to build up — a detail read that has not landed,
 * or a work order priced with no lines. The caller decides what to put there instead,
 * because "the total, on its own" and "nothing at all" are both right answers and it is
 * not this component's call which.
 */
export function QuoteBuildUpRows({
  lines,
  breakdown,
  totalLabel,
  total,
  discountLabel,
  emphasiseTotal,
  statesTier,
}: QuoteBuildUpProps) {
  const format = useFormat()
  if (breakdown === null || lines.length === 0) return null

  return (
    <>
      {lines.map((line) => (
        <Tr key={line.seq}>
          <Td>{line.description}</Td>
          <Td right num>
            <Num value={line.qty} />
          </Td>
          <Td right num>
            <Num value={line.amount} />
          </Td>
        </Tr>
      ))}

      <Tr variant="sub">
        <Td>
          {statesTier === true ? (
            <>
              Subtotal &middot; market tier {format.number(breakdown.tier.code)}, &times;
              {format.number(breakdown.tier.multiplier_bps / 10_000, { fractionDigits: 1 })}
            </>
          ) : (
            'Subtotal'
          )}
        </Td>
        <Td />
        <Td right num>
          <Num value={breakdown.tier.amount} />
        </Td>
      </Tr>

      {breakdown.retainer_discount_bps === 0 ? null : (
        <Tr>
          <Td>
            {discountLabel} &mdash; {format.percent(breakdown.retainer_discount_bps)} off the
            project
          </Td>
          <Td />
          <Td right num>
            <Num value={breakdown.retainer_discount} />
          </Td>
        </Tr>
      )}

      {breakdown.converted === null ? null : (
        <Tr>
          <Td>Converted to {breakdown.currency}</Td>
          <Td />
          <Td right num>
            <Num value={breakdown.converted} />
          </Td>
        </Tr>
      )}

      {isZero(breakdown.rounding) ? null : (
        <Tr>
          <Td>Rounded to $10</Td>
          <Td />
          <Td right num>
            <Num value={breakdown.rounding} />
          </Td>
        </Tr>
      )}

      <Tr variant="total">
        <Td>{totalLabel}</Td>
        <Td />
        <Td right num>
          <Num value={total} lg={emphasiseTotal === true} />
        </Td>
      </Tr>
    </>
  )
}

/** The same rows, in a table of their own. For a fold with nothing else in it. */
export function QuoteBuildUpTable(props: QuoteBuildUpProps) {
  if (props.breakdown === null || props.lines.length === 0) return null

  return (
    <ScrollX>
      <Table aria-label="What the fixed total is made of">
        <THead>
          <Tr>
            <Th>Quote line</Th>
            <Th right>Qty</Th>
            <Th right>Amount</Th>
          </Tr>
        </THead>
        <TBody>
          <QuoteBuildUpRows {...props} />
        </TBody>
      </Table>
    </ScrollX>
  )
}
